import { useEffect, useState } from 'react';
import {
  CROP_IDS,
  CROP_LABELS,
  plantCountFromAcreage,
  acreageFromPlantCount,
  type Farm,
  type Block,
  type KcRow,
  type CropId,
  type FarmStation,
} from './farmTypes';

export default function FarmsEditor({ initialFarmId }: { initialFarmId?: string | null }) {
  const [farms, setFarms] = useState<Farm[]>([]);
  const [kcTable, setKcTable] = useState<KcRow[]>([]);
  const [selectedFarmId, setSelectedFarmId] = useState(initialFarmId || 'olive');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch('/api/admin/farms');
        if (r.status === 403) {
          setError('Not signed in — reload the page to sign in with Cloudflare Access.');
          return;
        }
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const body = (await r.json()) as { farms?: Farm[]; kcTable?: KcRow[] };
        if (cancelled) return;
        const loaded = body.farms ?? [];
        setFarms(loaded);
        setKcTable(body.kcTable ?? []);
        // An unknown ?farm= id (or a deleted farm) would leave the editor blank.
        if (loaded.length > 0 && !loaded.some((f) => f.id === selectedFarmId)) {
          setSelectedFarmId(loaded[0].id);
        }
      } catch (err) {
        if (!cancelled) setError(String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    setSavedAt(null);
    try {
      const r = await fetch('/api/admin/farms', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ farms }),
      });
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      if (!r.ok) throw new Error(j.error ?? `HTTP ${r.status}`);
      setSavedAt(Date.now());
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  const selectedFarm = farms.find((f) => f.id === selectedFarmId);

  function updateFarm(next: Farm) {
    setFarms((fs) => fs.map((f) => (f.id === next.id ? next : f)));
  }

  function addBlock() {
    if (!selectedFarm) return;
    const crop: CropId = selectedFarm.id === 'olive' ? 'olive' : 'eucalyptus';
    const rowSpacingFt = 10;
    const inRowSpacingFt = 5;
    const acreage = 1;
    const newBlock: Block = {
      id: `${crop}-${crypto.randomUUID().slice(0, 6)}`,
      name: 'New block',
      crop,
      variety: null,
      acreage,
      rowSpacingFt,
      inRowSpacingFt,
      plantCount: plantCountFromAcreage(acreage, rowSpacingFt, inRowSpacingFt),
      emittersPerPlant: 2,
      gphPerEmitter: 1,
      canopyCoverPct: 50,
      matureCoverPct: 70,
      efficiencyPct: 90,
      kcOverride: null,
      notes: '',
      configured: true,
      sensors: [],
    };
    updateFarm({ ...selectedFarm, blocks: [...selectedFarm.blocks, newBlock] });
  }

  return (
    <div>
      <div className="flex items-center gap-2 flex-wrap mb-4">
        {farms.map((f) => (
          <button
            key={f.id}
            onClick={() => setSelectedFarmId(f.id)}
            className={`text-sm px-3 py-1.5 rounded ${
              selectedFarmId === f.id ? 'bg-emerald-600' : 'bg-slate-800'
            }`}
          >
            {f.name}
          </button>
        ))}
        <div className="flex-1" />
        <button
          onClick={addBlock}
          disabled={!selectedFarm}
          className="bg-emerald-600 hover:bg-emerald-500 rounded px-3 py-1.5 text-sm disabled:opacity-50"
        >
          + Add block
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-500 rounded px-3 py-1.5 text-sm disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save all'}
        </button>
      </div>

      {loading && <div className="text-sm opacity-60 mb-4">Loading…</div>}

      {error && (
        <div className="mb-4 p-3 rounded bg-rose-900/50 border border-rose-500/40 text-sm">{error}</div>
      )}

      {savedAt && (
        <div className="text-xs opacity-60 mb-4">Saved. The dashboard picks this up on its next refresh.</div>
      )}

      {selectedFarm && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-3 mb-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Name</div>
              <input
                value={selectedFarm.name}
                onChange={(e) => updateFarm({ ...selectedFarm, name: e.target.value })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Latitude</div>
              <input
                type="number"
                value={selectedFarm.lat}
                onChange={(e) => updateFarm({ ...selectedFarm, lat: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Longitude</div>
              <input
                type="number"
                value={selectedFarm.lon}
                onChange={(e) => updateFarm({ ...selectedFarm, lon: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">CIMIS station</div>
              <input
                value={selectedFarm.cimisStationId}
                onChange={(e) => updateFarm({ ...selectedFarm, cimisStationId: e.target.value })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Station</div>
              <select
                value={selectedFarm.station.kind}
                onChange={(e) => {
                  const kind = e.target.value as FarmStation['kind'];
                  updateFarm({
                    ...selectedFarm,
                    station: {
                      kind,
                      deviceId: kind === 'lora' ? selectedFarm.station.deviceId : null,
                    },
                  });
                }}
                className="w-full bg-slate-800 rounded px-2 py-1"
              >
                <option value="none">None (forecast grid only)</option>
                <option value="ambient-pws">Ambient PWS</option>
                <option value="lora">LoRa (LoraDash)</option>
              </select>
            </label>
            {selectedFarm.station.kind === 'lora' && (
              <label className="block">
                <div className="text-xs opacity-60 mb-1">LoRa device id</div>
                <input
                  value={selectedFarm.station.deviceId ?? ''}
                  onChange={(e) => {
                    const trimmed = e.target.value.trim();
                    updateFarm({
                      ...selectedFarm,
                      station: { ...selectedFarm.station, deviceId: trimmed === '' ? null : e.target.value },
                    });
                  }}
                  placeholder="blank = latest weather device"
                  maxLength={64}
                  className="w-full bg-slate-800 rounded px-2 py-1"
                />
              </label>
            )}
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Rain ignored below (in)</div>
              <input
                type="number"
                step="0.05"
                value={selectedFarm.rainThresholdIn}
                onChange={(e) => updateFarm({ ...selectedFarm, rainThresholdIn: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Rain effectiveness (%)</div>
              <input
                type="number"
                value={selectedFarm.rainEffectivenessPct}
                onChange={(e) =>
                  updateFarm({ ...selectedFarm, rainEffectivenessPct: Number(e.target.value) })
                }
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
          </div>
        </div>
      )}

      {selectedFarm && (
        <div className="space-y-3">
          {selectedFarm.blocks.map((b, idx) => (
            <BlockEditor
              key={b.id}
              block={b}
              kcTable={kcTable}
              onChange={(next) =>
                updateFarm({
                  ...selectedFarm,
                  blocks: selectedFarm.blocks.map((x, i) => (i === idx ? next : x)),
                })
              }
              onDelete={() =>
                updateFarm({
                  ...selectedFarm,
                  blocks: selectedFarm.blocks.filter((_, i) => i !== idx),
                })
              }
            />
          ))}
          {selectedFarm.blocks.length === 0 && (
            <div className="text-center opacity-60 py-12 text-sm">
              No blocks yet for {selectedFarm.name}. Click "+ Add block" to create one.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function resolveCropDefault(block: Block, kcTable: KcRow[]): KcRow | null {
  const variety = block.variety?.trim().toLowerCase() ?? null;
  const exact = kcTable.find(
    (r) => r.crop === block.crop && r.variety != null && r.variety.trim().toLowerCase() === variety,
  );
  if (exact) return exact;
  return kcTable.find((r) => r.crop === block.crop && r.variety == null) ?? null;
}

function BlockEditor({
  block,
  kcTable,
  onChange,
  onDelete,
}: {
  block: Block;
  kcTable: KcRow[];
  onChange: (b: Block) => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const cropDefault = resolveCropDefault(block, kcTable);

  function edit(patch: Partial<Block>) {
    onChange({ ...block, ...patch, configured: true });
  }

  function editAcreage(acreage: number) {
    edit({ acreage, plantCount: plantCountFromAcreage(acreage, block.rowSpacingFt, block.inRowSpacingFt) });
  }

  function editPlantCount(plantCount: number) {
    edit({ plantCount, acreage: acreageFromPlantCount(plantCount, block.rowSpacingFt, block.inRowSpacingFt) });
  }

  function editSpacing(rowSpacingFt: number, inRowSpacingFt: number) {
    edit({
      rowSpacingFt,
      inRowSpacingFt,
      plantCount: plantCountFromAcreage(block.acreage, rowSpacingFt, inRowSpacingFt),
    });
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg">
      <div className="flex items-center gap-3 p-3">
        <input
          value={block.name}
          onChange={(e) => edit({ name: e.target.value })}
          className="flex-1 bg-transparent text-sm font-medium focus:outline-none"
        />
        <div className="text-xs opacity-70">
          {CROP_LABELS[block.crop]}
          {block.variety ? ` · ${block.variety}` : ''} · {block.acreage} ac · {block.plantCount} plants
        </div>
        {!block.configured && (
          <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-100 border border-amber-400/40">
            Unconfigured
          </span>
        )}
        <button onClick={() => setOpen(!open)} className="text-xs px-2 py-1 bg-slate-800 rounded">
          {open ? 'Close' : 'Edit'}
        </button>
        <button onClick={onDelete} className="text-xs px-2 py-1 bg-rose-800 rounded">
          Delete
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-800 p-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Crop</div>
              <select
                value={block.crop}
                onChange={(e) => edit({ crop: e.target.value as CropId })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              >
                {CROP_IDS.map((c) => (
                  <option key={c} value={c}>
                    {CROP_LABELS[c]}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Variety</div>
              <input
                value={block.variety ?? ''}
                onChange={(e) => edit({ variety: e.target.value || null })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Acreage</div>
              <input
                type="number"
                value={block.acreage}
                onChange={(e) => editAcreage(Number(e.target.value))}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Plant count</div>
              <input
                type="number"
                value={block.plantCount}
                onChange={(e) => editPlantCount(Number(e.target.value))}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Row spacing (ft)</div>
              <input
                type="number"
                value={block.rowSpacingFt}
                onChange={(e) => editSpacing(Number(e.target.value), block.inRowSpacingFt)}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">In-row spacing (ft)</div>
              <input
                type="number"
                value={block.inRowSpacingFt}
                onChange={(e) => editSpacing(block.rowSpacingFt, Number(e.target.value))}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Emitters per plant</div>
              <input
                type="number"
                value={block.emittersPerPlant}
                onChange={(e) => edit({ emittersPerPlant: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">GPH per emitter</div>
              <input
                type="number"
                value={block.gphPerEmitter}
                onChange={(e) => edit({ gphPerEmitter: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Canopy cover (%)</div>
              <input
                type="number"
                value={block.canopyCoverPct}
                onChange={(e) => edit({ canopyCoverPct: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Mature canopy cover (%)</div>
              <input
                type="number"
                value={block.matureCoverPct}
                onChange={(e) => edit({ matureCoverPct: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Drip efficiency (%)</div>
              <input
                type="number"
                value={block.efficiencyPct}
                onChange={(e) => edit({ efficiencyPct: Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
            </label>
            <label className="block">
              <div className="text-xs opacity-60 mb-1">Kc override</div>
              <input
                type="number"
                step="0.01"
                value={block.kcOverride ?? ''}
                onChange={(e) => edit({ kcOverride: e.target.value === '' ? null : Number(e.target.value) })}
                className="w-full bg-slate-800 rounded px-2 py-1"
              />
              {cropDefault && (
                <div className="text-[10px] opacity-50 mt-1">
                  Crop default Kc {cropDefault.kc} — {cropDefault.source}
                </div>
              )}
              {block.kcOverride == null && (
                <div className="text-[10px] opacity-50 mt-1">Using default Kc — no calibration logged yet.</div>
              )}
            </label>
          </div>
          <label className="block">
            <div className="text-xs opacity-60 mb-1">Notes</div>
            <textarea
              value={block.notes}
              onChange={(e) => edit({ notes: e.target.value })}
              className="w-full bg-slate-800 rounded px-2 py-1 text-sm"
              rows={2}
            />
          </label>
        </div>
      )}
    </div>
  );
}
